<template>
  <div class="chart-ip-wrapper">
    <div class="title">访问统计</div>
    <div class="date-change-box">
      <el-radio-group v-model="dateType" fill="#003d92" size="medium" @change="dateTypeChange">
        <el-radio-button label="当天"></el-radio-button>
        <el-radio-button label="最近7天"></el-radio-button>
        <el-radio-button label="最近三十天"></el-radio-button>
        <el-radio-button label="自定义"></el-radio-button>
      </el-radio-group>
      <el-date-picker
        v-model="customDate"
        type="daterange"
        range-separator="至"
        start-placeholder="开始日期"
        end-placeholder="结束日期"
        size="small"
        value-format="yyyy-MM-dd"
        :picker-options="pickerOptions"
        v-if="showDatePicker"
        @change="getCustomDateData"
      ></el-date-picker>
    </div>
    <line-chart style="margin-bottom:32px;" :data="lineChartData" :pending="requestPending"></line-chart>
    <div class="pie-chart-wrapper">
      <left-pie :data="leftPieData" :pending="requestPending"></left-pie>
      <right-pie :data="rightPieData" :pending="requestPending"></right-pie>
    </div>
  </div>
</template>

<script>
import LineChart from './lineChart.vue'
import LeftPie from './leftPie.vue'
import RightPie from './rightPie.vue'
import { getIpEchartDataForHourly, getIpEchartDataForDaily } from '@/services/interface'
import { getDateForNum, formatDate } from '@/util/common'
export default {
  components: {
    LineChart,
    LeftPie,
    RightPie
  },
  props: {
    version: {
      type: String
    }
  },
  data () {
    return {
      dateType: '当天',
      lineChartData: {},
      leftPieData: {},
      rightPieData: {},
      requestPending: true,
      showDatePicker: false,
      customDate: [],
      pickerFirstDate: '', // 日期选择器选择的第一个时间
      pickerOptions: {
        onPick: ({ maxDate, minDate }) => { // 日期选择器选择的点击选择事件
          this.pickerFirstDate = minDate.getTime() // 点击下去第一个时间
          if (maxDate) { // 如果选择了第二个时间，重置pickerFirstDate
            this.pickerFirstDate = ''
          }
        },
        disabledDate: (time) => {
          if (this.pickerFirstDate !== '') { // 当只选择了第一个时间
            const day90 = (90 - 1) * 24 * 3600 * 1000
            let maxTime = this.pickerFirstDate + day90
            const minTime = this.pickerFirstDate - day90
            if (maxTime > new Date()) {
              maxTime = new Date()
            }
            return time.getTime() > maxTime || time.getTime() < minTime || time.getTime() < new Date('2020-07-07').getTime()
          }
          return time.getTime() > Date.now() || time.getTime() < new Date('2020-07-07').getTime()
        }
      }
    }
  },
  methods: {
    /**
       * 单选按钮组改变事件
       * @param label: 单选按钮的label值
       */
    dateTypeChange (label) {
      switch (label) {
        case '当天':
          this.showDatePicker = false
          this.getTodayData()
          break
        case '最近7天':
          this.showDatePicker = false
          this.getFixedDaysData(7)
          break
        case '最近三十天':
          this.showDatePicker = false
          this.getFixedDaysData(30)
          break
        case '自定义':
          this.customDate = []
          this.showDatePicker = true
          break
      }
    },

    /**
     * 获取当天数据
     */
    async getTodayData () {
      this.requestPending = true
      const params = {
        version: this.version
      }
      const res = await getIpEchartDataForHourly(params).then((res) => {
        return res
      })
      this.handleSetEchartData(res, true)
    },

    /**
     * 获取固定天数的数据
     * @param num: 天数
     */
    async getFixedDaysData (num) {
      this.requestPending = true
      let { start, end } = getDateForNum(num - 1)
      if (start.getTime() < new Date('2020-07-08').getTime()) {
        start = new Date('2020-07-08 00:00:00')
      }
      const params = {
        start_date: formatDate(start, 'yyyy-MM-dd hh:mm'),
        end_date: formatDate(end, 'yyyy-MM-dd hh:mm'),
        version: this.version
      }
      const res = await getIpEchartDataForDaily(params).then((res) => {
        return res
      })
      this.handleSetEchartData(res, false)
    },

    /**
     * 获取自定义日期数据
     * @param value: Array<yyyy-MM-dd>
     */
    async getCustomDateData (value) {
      if (!value) return
      this.requestPending = true
      const params = {
        start_date: value[0] + ' 00:00',
        end_date: value[1] + ' 00:00',
        version: this.version
      }
      const res = await getIpEchartDataForDaily(params).then((res) => {
        return res
      })
      this.handleSetEchartData(res, false)
    },

    /**
     * 统一调取各个图表处理数据的方法
     * @param res: 接口返回数据
     * @param isToday: 是否为获取当天的数据，x轴处理不同
     */
    handleSetEchartData (res, isToday = false) {
      this.setDataforLineEchart(res.data.risk_info_list, isToday)
      this.setDataforLeftEchart(res.data.req_statistic)
      this.setDataforRightEchart(res.data.risk_distributed)
      this.requestPending = false
    },

    /**
     * 处理折线图表数据
     * @param data: Array<[key:date]:{count:number,risk:string}[]>
     * @param isToday: 是否为获取当天的数据，x轴处理不同
     */
    setDataforLineEchart (data, isToday = false) {
      const xAxisData = []
      const seriesData = {}
      data.forEach((item) => {
        for (const k in item) {
          if (isToday) {
            xAxisData.push(k.substring(11))
          } else {
            xAxisData.push(k.substring(0, 11))
          }
          item[k].forEach((innerItem) => {
            if (!seriesData[innerItem.risk]) {
              seriesData[innerItem.risk] = []
            }
            seriesData[innerItem.risk].push(innerItem.count)
          })
        }
      })
      this.lineChartData = {
        xAxisData,
        seriesData
      }
    },

    /**
     * 处理左边饼图数据
     * @param data: Array<{count:number,tag:string}>
     */
    async setDataforLeftEchart (data) {
      const seriesData = {}
      data.forEach((item) => {
        seriesData[item.tag] = item.count
      })
      this.leftPieData = seriesData
    },

    /**
     * 处理右边饼图数据
     * @param data: Array<{count:number,risk:string}>
     */
    async setDataforRightEchart (data) {
      const seriesData = {}
      data.forEach((item) => {
        seriesData[item.risk] = item.count
      })
      this.rightPieData = seriesData
    }
  },
  created () {
    this.getTodayData()
  }
}
</script>

<style lang="scss" scoped>
.chart-ip-wrapper {
  margin-top: 40px;
  .title {
    font-size: 16px;
    font-weight: 500;
    color: rgba(112, 119, 141, 1);
    line-height: 22px;
    letter-spacing: 1px;
    margin-bottom: 15px;
  }
  .date-change-box {
    margin-bottom: 32px;
    display: flex;
    align-items: center;
  }
  .el-radio-group {
    margin-right: 20px;
  }
  .pie-chart-wrapper {
    display: flex;
    justify-content: space-between;
  }
}
</style>
